import type { CSSProperties, ReactNode } from 'react';
import { Children, useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router';

import classNames from 'classnames';

import styles from './SecondaryNavigation.module.css';

import { useViewport } from '../../hooks';
import { Button } from '../Button';

export interface SecondaryNavigationProps {
  className?: string;
  /** The CTA button(s) in the nav bar */
  callToActions?: ReactNode;
  /** The navigation item(s) in the nav bar */
  items: SecondaryNavigationItem[];
  /** Callback for when a navigation item is clicked on */
  onSelectItem?: (item: SecondaryNavigationItem) => void;
  /**
   * The navigation item that should be underlined if we don't want the
   * underline to respond to the current location url
   */
  activeRouteOverride?: string;
  /** The accessible label given to the nav bar */
  ['aria-label']?: string;
}

export interface SecondaryNavigationItem {
  to: string;
  label: string;
}

/**
 * Render a responsive navigation navbar.
 *
 * @beta
 * @see https://corgi-x.tfd.engineering/components/secondaryNavigation
 */
export function SecondaryNavigation({
  callToActions,
  className,
  items,
  onSelectItem,
  activeRouteOverride,
  'aria-label': ariaLabel,
}: SecondaryNavigationProps) {
  const location = useLocation();
  const [activeIndex, setActiveIndex] = useState(0);

  const refNav = useRef<HTMLUListElement>(null);
  const navigationRef = useRef<HTMLDivElement>(null);

  const navRefs = useRef<(HTMLLIElement | null)[]>([]);
  const [underlineStyle, setUnderlineStyle] = useState<CSSProperties>({});

  const { lg: isDesktop } = useViewport();

  const activeRoute = activeRouteOverride ?? location.pathname + location.hash;

  useEffect(() => {
    const index = items.findIndex(item => activeRoute.endsWith(item.to));
    if (index === activeIndex) {
      return;
    }
    if (index !== -1) {
      setActiveIndex(index);
    }
  }, [items, activeRoute, activeIndex]);

  const calculateUnderlineStyle = useCallback(() => {
    const activeLink = navRefs.current[activeIndex];
    if (!activeLink) {
      return;
    }

    const rect = activeLink.getBoundingClientRect();
    const containerRect = navigationRef.current?.getBoundingClientRect();

    setUnderlineStyle({
      width: `${activeLink.offsetWidth}px`,
      marginLeft: `${rect.x - (containerRect?.x ?? 0)}px`,
    });
  }, [activeIndex]);

  useEffect(() => {
    const activeLink = navRefs.current[activeIndex];
    if (!activeLink) {
      return;
    }
    activeLink?.scrollIntoView();
  }, [activeIndex]);

  useEffect(() => {
    calculateUnderlineStyle();
    window.addEventListener('resize', calculateUnderlineStyle);
    return () => window.removeEventListener('resize', calculateUnderlineStyle);
  }, [calculateUnderlineStyle, isDesktop]); // isDesktop is needed as a dependency because we are dynamically rendering the CTA based on its value. Since isDesktop evaluates to false on initial render, without including it as dependency, the bounding client rect of the first active link would be calculated incorrectly.
  return (
    <div className={styles.navContainer}>
      <nav
        className={classNames(className, styles.navbar)}
        aria-label={ariaLabel}
      >
        <div ref={navigationRef} className={styles.actionsContainer}>
          <ul className={styles.navList} ref={refNav}>
            {!isDesktop && Children.map(callToActions, item => item)}

            {items.map(({ label, to }, index) => (
              <li
                key={index}
                ref={el => (navRefs.current[index] = el)}
                className={styles.navItem}
              >
                <Button
                  type="link"
                  to={to}
                  color={activeIndex === index ? 'kale-dark' : 'kale'}
                  variant="plain-text"
                  size="sm"
                  onClick={() => {
                    navRefs.current[index]?.scrollIntoView();
                    if (onSelectItem) {
                      onSelectItem({ label, to });
                    }
                  }}
                >
                  {label}
                </Button>
              </li>
            ))}

            {isDesktop && Children.map(callToActions, item => item)}
          </ul>
          <div className={styles.activeUnderline} style={underlineStyle} />
        </div>
      </nav>
    </div>
  );
}
